/**
 * The lighting rig. ARCHITECTURE.md §6, §14.1.
 *
 * Coupled concern: lighting, tone mapping and exposure have ONE owner (src/render). If the image
 * reads flat, the cause is diagnosed among these three plus material response — never by crushing
 * albedo elsewhere.
 *
 * The rig is a three-point setup adapted for a fighting game:
 *   key      — the rooftop's practical overhead source, casts the shadows that ground the fighters
 *   rim/back — separates both silhouettes from the background. This is the single most important
 *              readability light in the whole game and is deliberately strong.
 *   fill     — a low, cool hemisphere standing in for skylight bounce off wet concrete
 * Plus two combat lights that respond to impacts without washing out the silhouettes.
 */

import * as THREE from 'three';
import { clamp, STAGE_HALF_WIDTH } from '../core/index.js';

export class LightingRig {
  constructor() {
    this.group = new THREE.Group();
    this.group.name = 'lighting';
    this.key = null;
    this.rim = null;
    this.fill = null;
    /** Transient impact lights, pooled — never allocated per hit. */
    this.combatLights = [];
    this._combatNext = 0;
    /** 0..1 storm ramp, driven by match state. */
    this.storm = 0;
  }

  init(ctx, bag) {
    const preset = ctx.config.quality;

    // --- key: overhead, slightly to the front-left of the combat axis ---
    const key = new THREE.DirectionalLight(0xfff1dc, 2.35);
    key.position.set(-3.2, 7.4, 4.6);
    key.target.position.set(0, 1.1, 0);
    key.castShadow = true;
    key.shadow.mapSize.setScalar(preset.shadowMapSize);
    // A tight ortho frustum around the fight area: shadow resolution is worth far more here
    // than covering the whole city, which is background and never receives shadows.
    const half = STAGE_HALF_WIDTH + 1.2;
    key.shadow.camera.left = -half;
    key.shadow.camera.right = half;
    key.shadow.camera.top = 5.0;
    key.shadow.camera.bottom = -1.0;
    key.shadow.camera.near = 0.5;
    key.shadow.camera.far = 22;
    key.shadow.bias = -0.0008;
    key.shadow.normalBias = 0.022;
    this.key = key;
    this.group.add(key);
    this.group.add(key.target);

    // --- rim: behind and above, opposing the key. Separates silhouettes. ---
    const rim = new THREE.DirectionalLight(0x9fd8ff, 3.1);
    rim.position.set(2.8, 4.2, -6.4);
    rim.target.position.set(0, 1.2, 0);
    rim.castShadow = false;
    this.rim = rim;
    this.group.add(rim);
    this.group.add(rim.target);

    // --- fill: cool sky above, warm bounce from the lit city below ---
    const fill = new THREE.HemisphereLight(0x4a6a8c, 0x2a1e18, 0.85);
    this.fill = fill;
    this.group.add(fill);

    // --- pooled combat lights ---
    for (let i = 0; i < 3; i++) {
      const light = new THREE.PointLight(0xffd9a0, 0, 4.2, 2.0);
      // Keep every pooled point-light slot visible even at zero intensity. Three.js includes the
      // visible point-light COUNT in each lit material's program key; toggling visibility when an
      // impact fires would compile variants for 3, 4, 5, and 6 lights during the match. A
      // zero-intensity visible light contributes exactly zero while keeping that key stable.
      light.visible = true;
      light.castShadow = false;
      this.combatLights.push({ light, ticks: 0, total: 1, peak: 0 });
      this.group.add(light);
    }

    bag.custom(() => {
      this.key.dispose();
      this.rim.dispose();
      this.fill.dispose();
      for (const c of this.combatLights) c.light.dispose();
    });

    this._unsub = ctx.events.on('fx:impact', (p) => this._flash(p));
    bag.custom(() => this._unsub && this._unsub());
    return this.group;
  }

  /**
   * Fires a short local light at an impact. Bounded to the pool: an impact during a 12-hit combo
   * reuses the oldest light rather than allocating.
   */
  _flash(p) {
    const strong = p.strength >= 0.9;
    const slot = this.combatLights[this._combatNext];
    this._combatNext = (this._combatNext + 1) % this.combatLights.length;
    slot.light.position.set(p.x, p.y, p.z);
    slot.peak = clamp(p.strength, 0.2, 1.6) * (strong ? 5.2 : 2.6);
    slot.total = strong ? 12 : 7;
    slot.ticks = slot.total;
    // Counter-hits read cooler and harsher; ordinary hits are warm.
    slot.light.color.setHex(p.kind === 'spark-counter' ? 0xbfe4ff : 0xffd9a0);
  }

  /** Decays combat lights. Called once per rendered frame. */
  update(dt, ctx) {
    for (const c of this.combatLights) {
      if (c.ticks <= 0) continue;
      c.ticks--;
      const t = c.ticks / c.total;
      c.light.intensity = c.peak * t * t;
      if (c.ticks <= 0) {
        c.light.intensity = 0;
      }
    }

    // Storm ramp: the key cools and dims, the rim hardens. Driven by match state so the final
    // round reads visually different without any asset swap.
    const match = ctx.sim.match;
    const target = match ? match.storm : 0;
    this.storm += (target - this.storm) * Math.min(1, (dt || 0.016) * 0.9);
    this._applyStorm();
    void ctx;
  }

  applyQuality(preset) {
    this.key.shadow.mapSize.setScalar(preset.shadowMapSize);
    if (this.key.shadow.map) {
      this.key.shadow.map.dispose();
      this.key.shadow.map = null;
    }
  }

  /** Forces the storm ramp to a value immediately. Used by deterministic captures. */
  setStorm(v) {
    this.storm = clamp(v, 0, 1);
    this._applyStorm();
  }

  /**
   * Clears every transient light and applies the storm value directly.
   *
   * Required for capture reproducibility: combat lights decay inside `update`, which only runs on
   * a presented frame, so a scenario used to inherit a partially-decayed impact light from
   * whatever ran before it — measured leaking across five consecutive scenarios at
   * 1.27 → 0.88 → 0.56 → 0.32 → 0.14 intensity, producing different pixels every time.
   */
  reset() {
    for (const c of this.combatLights) {
      c.ticks = 0;
      c.total = 1;
      c.peak = 0;
      c.light.intensity = 0;
      c.light.visible = true;
    }
    this._combatNext = 0;
    this._applyStorm();
  }

  /** Writes the storm-dependent light values without any smoothing. */
  _applyStorm() {
    const s = this.storm;
    if (!this.key) return;
    this.key.intensity = 2.35 - s * 1.05;
    this.key.color.setRGB(1, 0.945 - s * 0.06, 0.862 - s * 0.10);
    this.rim.intensity = 3.1 + s * 1.5;
    this.fill.intensity = 0.85 - s * 0.28;
  }
}
